import { api } from '@most/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChatConversation, ChatMessage, ChatMessagePage } from '@most/api-types';
import type { CallApi, CallKind, ChatCallDTO } from '@most/webrtc-call';
import { qk } from './keys';

/**
 * BUY support chat. Everything here talks to the /chat endpoints, which are scoped to the
 * caller's OWN thread server-side — there is no conversation id to pass and none to leak.
 */

export function useChatConversation(enabled = true) {
  return useQuery({
    queryKey: qk.chat,
    queryFn: ({ signal }) => api.get<ChatConversation>('/chat', { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useChatMessages(enabled = true) {
  return useInfiniteQuery({
    queryKey: qk.chatMessages,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.get<ChatMessagePage>('/chat/messages', { query: { cursor: pageParam }, signal }),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled,
    staleTime: 10_000,
  });
}

function invalidateThread(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: qk.chatMessages });
  void queryClient.invalidateQueries({ queryKey: qk.chat });
}

export function useSendChatMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.post<ChatMessage>('/chat/messages', { body }),
    onSuccess: () => invalidateThread(queryClient),
  });
}

export type ChatUploadKind = 'image' | 'video' | 'audio' | 'file';

export function useUploadChatAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file, caption }: { kind: ChatUploadKind; file: File | Blob; caption?: string }) => {
      const fd = new FormData();
      fd.append('kind', kind);
      if (caption) fd.append('body', caption);
      fd.append('file', file, file instanceof File ? file.name : 'recording.webm');
      return api.upload<ChatMessage>('/chat/attachments', fd);
    },
    onSuccess: () => invalidateThread(queryClient),
  });
}

export function useMarkChatRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ updated_count: number }>('/chat/read'),
    onSuccess: () => invalidateThread(queryClient),
  });
}

/** The transport adapter @most/webrtc-call asks for. */
export const chatCallApi: CallApi = {
  start: (kind: CallKind) => api.post<ChatCallDTO>('/chat/calls', { kind }),
  get: (id) => api.get<ChatCallDTO>(`/chat/calls/${id}`),
  accept: (id) => api.post<ChatCallDTO>(`/chat/calls/${id}/accept`),
  decline: (id) => api.post<ChatCallDTO>(`/chat/calls/${id}/decline`),
  end: (id) => api.post<ChatCallDTO>(`/chat/calls/${id}/end`),
  postSignal: async (id, data) => {
    await api.post(`/chat/calls/${id}/signals`, { data });
  },
  getSignals: async (id) => {
    const res = await api.get<{ signals: string[] }>(`/chat/calls/${id}/signals`);
    return res.signals;
  },
};
